"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";

import { FilterSelect } from "@/components/search/FilterSelect";
import { PannelloRicerca } from "@/components/search/PannelloRicerca";
import { ETICHETTA_FACCETTA, IconaGiu, IconaX } from "@/components/search/Faccette";
import { FUOCO } from "@/components/ui/pulsanti";
import { RITARDO_FILTRI } from "@/lib/constants";

/** Sopra questo numero di voci il menu si apre con il campo "Cerca…"
 *  dentro il pannello. Sotto, scorrerle è più rapido che scriverne una. */
const VOCI_CERCABILI = 8;

export type VoceMenu = {
  value: string;
  label: string;
  /** Tinta della voce, resa come pallino davanti all'etichetta. La usa
   *  il menu dei tipi per non perdere i toni editoriali. */
  tone?: string;
};

/** Un restringimento, dichiarato e non disegnato.
 *
 *  È la forma con cui una pagina dice **che cosa** può restringere; il
 *  come — la tendina sul sito, la sezione dentro il pannello del
 *  telefono, il parametro nell'indirizzo — lo decide la barra. Una
 *  pagina che non ha i paesi non passa il menu e non disegna un menu
 *  vuoto, ma la barra resta la stessa. */
export type MenuBarra = {
  /** Il nome del parametro nell'indirizzo: `place`, `cat`, `date`,
   *  `priority`, `area`. In modalità `locale` è solo una chiave. */
  param: string;
  /** Il glifo davanti all'etichetta: ◉ posto, ▣ tema, ◔ periodo,
   *  ◆ tipo. Lo stesso filtro porta lo stesso segno su tutto il sito. */
  icona: string;
  /** Una parola: «Paese», «Categoria», «Periodo», «Tipo».
   *  Non la frase intera («Tutti i Paesi»): quattro menu a etichetta
   *  lunga chiedevano 600 pixel e al campo ne restavano 130 a 1024. Un
   *  menu senza scelta è per definizione «tutti». */
  etichetta: string;
  opzioni: VoceMenu[];
  valore: string | string[];
  /** Più scelte insieme. La usa il tipo, perché a monte il filtro sul
   *  `type` è già un `$or` fra gli stem: chiederne tre costa una
   *  richiesta come uno. Nell'indirizzo vanno separati da virgola. */
  multiplo?: boolean;
  /** Forza il campo "Cerca…" dentro il pannello. Di suo si accende
   *  sopra VOCI_CERCABILI voci. */
  cercabile?: boolean;
  /** Il testo guida di quel campo. */
  segnaposto?: string;
  larghezzaPannello?: number;
  ariaLabel: string;
  /** La voce "nessuna scelta" scritta nel controllo, come sul Rullo
   *  («Tutte le categorie»). Senza, la ricava `vuotoDi` dall'etichetta. */
  vuoto?: string;
};

/** La voce "nessuna scelta" dei menu, con le parole del Rullo
 *  (components/rullo/RicercaRullo.tsx). */
export function vuotoDi(etichetta: string): string {
  const e = etichetta.toLowerCase();
  if (e.includes("paes") || e.includes("area") || e.includes("regione")) return "Tutti i Paesi e le aree";
  if (e.includes("categor")) return "Tutte le categorie";
  if (e.includes("period") || e.includes("data")) return "Qualsiasi data";
  if (e.includes("tipo")) return "Tutti i tipi";
  return "Tutti";
}

type Comuni = {
  /** Dove si sta cercando: «Rullo», «Dossier», «Serbia», «Balcani».
   *
   *  È una targhetta dentro la cornice, subito dopo la lente, e non una
   *  frase dentro il campo. Prima il contesto stava tutto nel
   *  segnaposto — «Cerca uno stretto, un corridoio, un tubo…», «Cerca
   *  fra i dispacci · Serbia» — quindi era lungo, diverso a ogni rotta,
   *  e **spariva appena si cominciava a scrivere**, cioè proprio quando
   *  serve sapere in che cosa si sta cercando. Nella targhetta resta.
   *
   *  Con `removeHref` porta una ✕: serve dove il contesto è un filtro
   *  che si può togliere (un'entità arrivata da un articolo), non dove
   *  è la pagina stessa. */
  contesto?: { label: string; removeHref?: string };
  /** Il testo guida del campo.
   *
   *  Di suo è uguale su tutto il sito: il *dove* lo dice la targhetta
   *  del contesto, qui resta il *cosa*. */
  segnaposto?: string;
  /** L'etichetta accessibile del campo. Un campo di ricerca senza nome
   *  è un campo di ricerca senza nome anche quando la parola non si
   *  vede in pagina. */
  ariaLabel: string;
  menu?: MenuBarra[];
  /** Le classi del contenitore esterno. Servono per lo stacco verticale
   *  e per l'ordine nel flusso, non per la larghezza: quella è la
   *  stessa ovunque e la decide la fascia. */
  className?: string;
  /** Il fondo dietro la barra, con un po' di respiro sopra e sotto.
   *
   *  **Spento di suo**, e la ragione è che non è decorazione: serve
   *  solo alla barra *appesa*, che deve coprire quello che le scorre
   *  sotto. Dove la barra sta ferma in mezzo a una pagina è una lastra
   *  che non copre niente — e si vede: sui dossier cadeva fra il bordo
   *  del frontespizio e l'occhiello «In apertura», cioè due righe
   *  lunghe e parallele a quaranta pixel l'una dall'altra con la barra
   *  schiacciata in mezzo. Là la barra deve appoggiarsi alla pagina,
   *  non stare incollata su una mascherina.
   *
   *  Accenderla accende anche `appesa`: sono la stessa decisione presa
   *  una volta sola. Oggi la vuole il solo rullo, dove la barra è il
   *  primo blocco sotto la testata ed è l'apparato della pagina. */
  fascia?: boolean;
  /** Appesa in cima alla finestra mentre si scorre, come il rullo.
   *
   *  L'aggancio è il bordo della finestra (`top-0`) e non
   *  `--oi-header-h`: la testata non resta appesa su nessuna rotta (vedi
   *  <StickyTopStack>), quindi sopra la barra, a pagina scorsa, non c'è
   *  niente da scavalcare. Agganciata all'altezza della testata restava
   *  ferma a 171 pixel dal bordo con un buco vuoto sopra.
   *
   *  **Non sotto `md`**: sul telefono la riga è l'apparato di una pagina
   *  che si legge scorrendo, e tenerla ferma toglie schermo per sempre.
   *  `static` e non `relative`, perché con `relative` il `top`
   *  sposterebbe davvero il blocco verso il basso. */
  appesa?: boolean;
  /** Appesa **fin dal caricamento**, e a qualunque larghezza.
   *
   *  La barra appesa normale ha la sua aria sopra, quindi al
   *  caricamento sta più in basso del punto in cui si fermerà: nei
   *  primi pixel di scorrimento sale, poi si blocca. È un movimento
   *  che si nota, perché la barra è l'unica cosa ferma mentre tutto il
   *  resto scorre, e sembra che parta in ritardo.
   *
   *  Con `ancorata` l'aria sopra non è più un margine della sezione ma
   *  un **rientro dentro la fascia**, dipinto come lei: la posizione
   *  nel flusso coincide così con quella di blocco — la barra è già
   *  dove si fermerebbe e non si muove mai — e lo stacco dalla
   *  navigazione resta lo stesso a pagina ferma e a pagina scorsa,
   *  invece di chiudersi al primo tocco di rotella. E vale anche sotto
   *  `md`: dove la barra è l'apparato principale della pagina, sul
   *  telefono serve più che altrove, e la riga compatta là è alta
   *  quanto un bottone.
   *
   *  Non è il valore di serie perché sul rullo la barra ha l'aria
   *  sopra per scelta — là sotto `md` non è appesa affatto — e perché
   *  senza aria sopra la barra va bene solo dove è il primo blocco
   *  della pagina: in mezzo a una pagina resterebbe incollata a quello
   *  che ha sopra. */
  ancorata?: boolean;
  /** Quello che chiude la riga a destra: di solito un contatore.
   *  Sparisce sotto `lg` e non sotto `sm`, perché con tre o quattro
   *  menu sulla stessa riga, sotto i 1024 pixel mandava a capo il
   *  campo e la riga tornava a essere due. */
  contatore?: ReactNode;
  /** Una riga sotto la cornice, per dove il contatore non c'è. */
  nota?: ReactNode;
  /** Una navigazione è in volo: la riga accende il filo in cima. */
  isLoading?: boolean;
  /** Il pannello a schermo intero sul telefono.
   *
   *  Di suo si accende dove la barra interroga un archivio remoto e ha
   *  dei restringimenti: là sotto `sm` la riga non ci sta e i controlli
   *  si schiacciano, e le scelte si consegnano comunque tutte insieme
   *  al tocco di «Applica».
   *
   *  In modalità `locale` no, e non è una dimenticanza: là il campo
   *  filtra **mentre si scrive** l'elenco che sta lì sotto, e una
   *  pagina a schermo intero coprirebbe proprio la lista che si sta
   *  guardando cambiare. Il mestiere è diverso, quindi la variante
   *  mobile è diversa: la cornice resta in pagina e va a capo dentro
   *  di sé. */
  conPannello?: boolean;
};

/** L'indirizzo è lo stato: la barra scrive `?q=`, `?cat=`, `?place=`,
 *  `?date=`, `?priority=`, azzera `?page=` e la pagina rifà la domanda
 *  a monte. È la convenzione del sito, e vale per i filtri che
 *  interrogano un archivio remoto. */
type ModoUrl = Comuni & {
  modo: "url";
  /** L'indirizzo della pagina che rende la barra, senza query stringa. */
  basePath: string;
  /** L'indirizzo corrente **già serializzato**, `q` compresa.
   *
   *  Arriva come stringa e non si legge con `useSearchParams` di
   *  proposito: quell'hook obbliga chi rende a un `<Suspense>` attorno,
   *  e qui non c'è niente da aspettare — la pagina i parametri li ha
   *  già in mano. È così che questa barra si può rendere da un Server
   *  Component senza portare la rotta al render dinamico. */
  parametri: string;
  /** Il testo corrente (`?q=`), già ripulito da chi rende. */
  testo?: string;
  /** Come navigare, se non basta un `router.push`. Lo passa il rullo,
   *  che avvolge la navigazione in una `startTransition` per tenere
   *  acceso il filo e smorzare la lista mentre la richiesta è in volo,
   *  e che non vuole lo scorrimento in cima a ogni filtro. Il **dove**
   *  resta della barra: qui si dice solo il come. */
  naviga?: (href: string) => void;
};

/** Filtra un elenco **già in pagina**, sul client, senza toccare
 *  l'indirizzo. Serve dove le voci sono un file del repo e non una
 *  domanda a un archivio: l'indice dei paesi e quello dei corridoi, che
 *  così restano statiche (`○` nel build). Non c'è niente da rifare sul
 *  server a ogni lettera, e mettere quel filtro nell'indirizzo
 *  renderebbe dinamica una pagina che non ha bisogno di esserlo.
 *
 *  Stessa faccia, mestiere diverso: è la sola differenza fra le due
 *  modalità, e sta tutta in chi riceve la scelta. */
type ModoLocale = Comuni & {
  modo: "locale";
  testo: string;
  onTesto: (v: string) => void;
  onMenu?: (param: string, valore: string | string[]) => void;
  /** Che cosa fa l'Invio, dove non c'è niente da mandare a monte:
   *  l'indice dei paesi apre la prima scheda che combacia. Senza,
   *  l'Invio non fa nulla e il pulsante non si rende. */
  onInvio?: () => void;
};

type Props = ModoUrl | ModoLocale;

/**
 * La barra di ricerca del sito: **una sola**.
 *
 * Ce n'erano sette, e sei condividevano già le classi del campo
 * (`searchField.ts`) ma non il montaggio: ognuna rimetteva insieme a
 * mano lente, menu, campo, ✕ e pulsante, decidendo per conto suo la
 * larghezza, le regole di andata a capo, se un pulsante ci fosse e dove
 * mettere il contatore. Misurato prima dell'unificazione, a 390 pixel
 * le cornici erano alte 39, 52,5, 63 e 79,5 pixel a seconda della
 * pagina, e il pannello a schermo intero ce l'aveva solo il rullo.
 * Un campo che fa la stessa cosa e si presenta in quattro modi si legge
 * come quattro funzioni diverse.
 *
 * Qui il montaggio è uno: lente, i restringimenti dentro la cornice, il
 * campo, la ✕, il pulsante; sotto `sm` un bersaglio solo che apre
 * <PannelloRicerca>. Quello che cambia da una pagina all'altra è
 * **dichiarato**, non ridisegnato: quali menu (una pagina senza paesi
 * non passa il menu dei paesi e non ne disegna uno vuoto) e in quale
 * delle due modalità, `url` o `locale`, che hanno la stessa faccia e
 * due mestieri diversi — vedi i due tipi qui sopra.
 *
 * Il campo è un `<form method="get">` vero anche quando poi la
 * navigazione la fa il router: l'`action` e i campi nascosti fanno
 * funzionare la ricerca prima che il JavaScript arrivi, l'`onSubmit`
 * intercettato la fa diventare una navigazione client quando c'è. La
 * ✕ è un `<Link>` per la stessa ragione, e così funziona anche col
 * tasto centrale e in una scheda nuova.
 */
export function BarraRicerca(props: Props) {
  const {
    contesto,
    segnaposto = "Cerca una parola, un nome…",
    ariaLabel,
    menu = [],
    className = "",
    contatore,
    nota,
    isLoading = false,
    conPannello = props.modo === "url" && menu.length > 0,
    fascia = false,
    appesa = fascia,
    ancorata = false,
  } = props;
  const router = useRouter();
  const [pannelloAperto, setPannelloAperto] = useState(false);

  const urlMode = props.modo === "url";
  const testoUrl = urlMode ? props.testo ?? "" : props.testo;

  /** Il testo mentre si scrive. L'indirizzo resta lo stato — è lui che
   *  fa rifare la domanda a monte — ma ogni lettera non può essere una
   *  navigazione.
   *
   *  Si riallinea all'indirizzo **durante il render** e non in un
   *  effetto: arrivando da un link con `?q=` dentro, o premendo il
   *  tasto indietro, il campo deve mostrare quello che la lista sta
   *  davvero filtrando. */
  const [testo, setTesto] = useState(testoUrl);
  const [testoPrec, setTestoPrec] = useState(testoUrl);
  if (testoUrl !== testoPrec) {
    setTestoPrec(testoUrl);
    setTesto(testoUrl);
  }

  /** Lo specchio dei menu, per la finestra fra il clic e la lista nuova.
   *
   *  Serve da quando i tipi si scelgono a più d'uno: i filtri stanno
   *  nell'indirizzo e fra il clic e la risposta a monte passa mezzo
   *  secondo, in cui `props` porta ancora i valori di prima. Il secondo
   *  clic calcolerebbe la lista nuova partendo da una selezione vuota e
   *  cancellerebbe il primo.
   *
   *  Come il campo, si riallinea a quello che arriva da fuori: può
   *  anticipare la lista ma non contraddirla. Appena la navigazione
   *  atterra, quello che si vede è quello che è stato chiesto. */
  const chiaveMenu = JSON.stringify(menu.map((m) => m.valore));
  const [mostrati, setMostrati] = useState<Record<string, string | string[]>>(
    () => Object.fromEntries(menu.map((m) => [m.param, m.valore]))
  );
  const [chiavePrec, setChiavePrec] = useState(chiaveMenu);
  if (chiaveMenu !== chiavePrec) {
    setChiavePrec(chiaveMenu);
    setMostrati(Object.fromEntries(menu.map((m) => [m.param, m.valore])));
  }

  const attesa = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (attesa.current) clearTimeout(attesa.current);
    },
    []
  );

  const vaiA = useCallback(
    (href: string) => {
      if (urlMode && props.naviga) props.naviga(href);
      else router.push(href);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `props.naviga` si ricrea a ogni render del chiamante; quello che conta è che ci sia
    [urlMode, router]
  );

  /** Compone l'indirizzo a partire da quello corrente, sovrascrivendo
   *  le coppie passate. `page` e `before` cadono sempre: un
   *  restringimento nuovo è una lettura nuova, e restare alla settima
   *  pagina di un'altra domanda dà una lista vuota senza spiegare
   *  perché. */
  const componi = useCallback(
    (cambi: Record<string, string>) => {
      const p = new URLSearchParams(urlMode ? props.parametri : "");
      for (const [k, v] of Object.entries(cambi)) {
        if (v) p.set(k, v);
        else p.delete(k);
      }
      p.delete("page");
      p.delete("before");
      const qs = p.toString();
      const base = urlMode ? props.basePath : "";
      return qs ? `${base}?${qs}` : base;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- le due letture di props sotto `urlMode` sono stabili quanto i valori che la pagina passa
    [urlMode, urlMode ? props.parametri : "", urlMode ? props.basePath : ""]
  );

  /** Un menu è cambiato. Sul sito remoto: lo specchio si accende
   *  subito, la navigazione parte dopo la pausa, e una raffica di clic
   *  su menu diversi diventa **una** navigazione con tutti i valori,
   *  perché l'indirizzo si ricompone dallo specchio aggiornato e non
   *  dai props, che sono ancora indietro. */
  const cambiaMenu = (m: MenuBarra, valore: string | string[]) => {
    if (props.modo === "locale") {
      setMostrati((p) => ({ ...p, [m.param]: valore }));
      props.onMenu?.(m.param, valore);
      return;
    }
    const prossimi = { ...mostrati, [m.param]: valore };
    setMostrati(prossimi);
    const cambi: Record<string, string> = {};
    for (const [k, v] of Object.entries(prossimi))
      cambi[k] = Array.isArray(v) ? v.join(",") : v;
    // Paese ed entità sono restringimenti alternativi: tenerli insieme
    // darebbe una lista vuota senza spiegare perché.
    if (m.param === "place" && valore) {
      cambi.etype = "";
      cambi.entity = "";
    }
    const href = componi(cambi);
    if (attesa.current) clearTimeout(attesa.current);
    attesa.current = setTimeout(() => vaiA(href), RITARDO_FILTRI);
  };

  const inviaTesto = (valore: string) => {
    if (props.modo === "locale") {
      props.onInvio?.();
      return;
    }
    vaiA(componi({ q: valore.trim() }));
  };


  /** Quanti restringimenti sono accesi. Sul bersaglio compatto del
   *  telefono è l'unico modo per accorgersi che un filtro è rimasto
   *  acceso, visto che i menu non si vedono. */
  const quantiAccesi =
    menu.reduce((n, m) => {
      const v = mostrati[m.param];
      return n + (Array.isArray(v) ? v.length : v ? 1 : 0);
    }, 0) + (testoUrl ? 1 : 0);

  /** I campi nascosti del modulo: un `GET` spedisce solo quello che ha
   *  dentro, quindi senza questi una ricerca scritta a filtri accesi li
   *  azzererebbe tutti. `q` no (la scrive il campo), `page` e `before`
   *  nemmeno (una ricerca nuova riparte da pagina uno). */
  const nascosti = urlMode
    ? [...new URLSearchParams(props.parametri).entries()].filter(
        ([k]) => k !== "q" && k !== "page" && k !== "before"
      )
    : [];

  const hrefAzzeraTesto = urlMode ? componi({ q: "" }) : "";
  const idCampo = useId();

  /* La colonna dei menu, scritta per esteso per ogni numero di menu:
     Tailwind genera solo le classi che trova scritte nel sorgente. */
  const colonneMenu =
    menu.length >= 4
      ? "sm:grid-cols-2 lg:grid-cols-4"
      : menu.length === 3
        ? "sm:grid-cols-2 lg:grid-cols-3"
        : menu.length === 2
          ? "sm:grid-cols-2"
          : "";

  const laCornice = (
    /* La forma del pannello di ricerca del Rullo (30/09/2026).

       Il sito aveva tre barre di ricerca per la stessa azione: il
       pannello del Rullo (etichetta, campo, «Cerca» sabbia, e sotto un
       filetto i filtri con le loro etichette), il riquadro della prima
       pagina, e questa, una cornice compatta con la lente, i menu in
       maiuscoletto mono dentro la cornice e il testo guida in
       monospazio. Su /report, /regioni, /corridoi e /archivio la stessa
       azione si leggeva in un altro modo. Adesso questa barra ha la
       pelle del Rullo, fatta con gli stessi pezzi
       (components/search/Faccette.tsx): riga del campo in alto, riga dei
       menu sotto, ciascuno con la sua etichetta. Logica, indirizzi,
       modo locale e pannello del telefono non cambiano.

       `max-sm:hidden` quando c'è il pannello: sotto `sm` al suo posto
       c'è il bersaglio che lo apre. */
    <div
      className={`min-w-0 flex-1 border border-rule bg-surface ${
        conPannello ? "max-sm:hidden" : ""
      }`}
    >
      {/* Il modulo sta attorno al solo campo e non ai menu: il pannello
          dei menu porta un campo "Cerca una voce…", e un `<input>` dentro
          un `<form>` manda il modulo appena si preme Invio. */}
      <form
        {...(urlMode ? { method: "get", action: props.basePath } : {})}
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          inviaTesto(testo);
        }}
        className="p-4 md:p-5"
      >
        <div className="flex flex-wrap items-center justify-between gap-x-3">
          <label htmlFor={idCampo} className={ETICHETTA_FACCETTA}>
            {ariaLabel}
          </label>
          {/* Dove si cerca, quando la barra sta dentro una fetta
              (un paese, una regione): lo stesso filtro acceso del
              Rullo, col pallino e la ✕ per toglierlo. */}
          {contesto?.removeHref ? (
            <Link
              href={contesto.removeHref}
              aria-label={`Togli il filtro ${contesto.label}`}
              className={`mb-1.5 inline-flex items-center gap-1.5 font-mono text-nota font-semibold uppercase tracking-etichetta text-accent no-underline hover:text-ink ${FUOCO}`}
            >
              <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-accent" />
              {contesto.label}
              <IconaX className="h-3 w-3" />
            </Link>
          ) : null}
        </div>

        {nascosti.map(([k, v], i) => (
          <input key={`${k}-${i}`} type="hidden" name={k} value={v} />
        ))}

        <div className="flex min-h-[52px] min-w-0 items-stretch border border-sommesso/60 bg-bg focus-within:border-accent">
          {/* 17 pixel (`text-titolo`): sotto i 16 iOS ingrandisce la
              pagina al fuoco. */}
          <input
            id={idCampo}
            type="search"
            name="q"
            value={testo}
            onChange={(e) => {
              setTesto(e.target.value);
              if (props.modo === "locale") props.onTesto(e.target.value);
            }}
            placeholder={segnaposto}
            autoComplete="off"
            enterKeyHint="search"
            className="min-w-0 flex-1 border-0 bg-transparent px-[15px] font-sans text-titolo text-ink placeholder:text-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent [&::-webkit-search-cancel-button]:hidden"
          />

          {testo ? (
            urlMode ? (
              /* Un `<Link>`: azzerare una ricerca è una navigazione, e
                 così funziona anche a JavaScript spento. */
              <Link
                href={hrefAzzeraTesto}
                onClick={() => setTesto("")}
                aria-label="Azzera la ricerca"
                className={`inline-flex w-11 shrink-0 items-center justify-center text-muted no-underline hover:text-ink ${FUOCO}`}
              >
                <IconaX className="h-4 w-4" />
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setTesto("");
                  props.onTesto("");
                }}
                aria-label="Azzera la ricerca"
                className={`inline-flex w-11 shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent text-muted hover:text-ink ${FUOCO}`}
              >
                <IconaX className="h-4 w-4" />
              </button>
            )
          ) : null}

          {/* Il conto, dove è una risposta (su /report compare solo a
              filtro acceso): da `lg` dentro la riga, sotto nella nota. */}
          {contatore ? (
            <div className="hidden shrink-0 items-center gap-1 whitespace-nowrap border-l border-rule px-3.5 font-mono text-nota font-semibold uppercase tracking-dato text-muted lg:flex">
              {isLoading ? <span className="text-accent">Caricamento…</span> : contatore}
            </div>
          ) : null}

          {/* «Cerca» sabbia, come sul Rullo. Dove l'Invio non ha niente
              da mandare (un elenco che si filtra mentre si scrive) il
              pulsante non si rende. */}
          {props.modo === "url" || props.onInvio ? (
            <button
              type="submit"
              className={`inline-flex w-[120px] shrink-0 cursor-pointer items-center justify-center border-0 bg-sabbia font-sans text-voce font-semibold text-suSabbia transition-opacity hover:opacity-90 max-[359px]:w-[88px] ${FUOCO}`}
            >
              Cerca
            </button>
          ) : null}
        </div>
      </form>

      {/* I menu, sotto un filetto, ciascuno con la sua etichetta: la riga
          dei filtri del Rullo. */}
      {menu.length > 0 ? (
        <div className={`grid gap-4 border-t border-rule p-4 md:px-5 ${colonneMenu}`}>
          {menu.map((m) => (
            <div key={m.param} className="min-w-0">
              <span aria-hidden className={ETICHETTA_FACCETTA}>
                {m.etichetta}
              </span>
              <FilterSelect
                variante="faccetta"
                {...(m.multiplo
                  ? {
                      multiple: true as const,
                      value: (mostrati[m.param] as string[]) ?? [],
                      onChange: (v: string[]) => cambiaMenu(m, v),
                    }
                  : {
                      value: (mostrati[m.param] as string) ?? "",
                      onChange: (v: string) => cambiaMenu(m, v),
                    })}
                icon={m.icona}
                placeholder={m.vuoto ?? vuotoDi(m.etichetta)}
                options={m.opzioni}
                searchable={m.cercabile ?? m.opzioni.length > VOCI_CERCABILI}
                searchPlaceholder={m.segnaposto}
                panelMinWidth={m.larghezzaPannello ?? 240}
                ariaLabel={m.ariaLabel}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );

  const riga = (
    <>
      <div className="relative flex flex-wrap items-center gap-x-4 gap-y-2">
        {/* Il filo che dice che una domanda è partita. */}
        {isLoading ? (
          <div
            aria-hidden
            className="absolute inset-x-0 -top-2 h-[2px] animate-pulse bg-accent"
          />
        ) : null}

        {/* TELEFONO — un bersaglio solo, che apre i restringimenti a
            schermo intero. A 390 pixel la riga del sito misura più del
            viewport, e i controlli restano illeggibili anche mandandoli
            a capo: meglio una riga pulita qui e tutto lo spazio che
            serve dentro il pannello. */}
        {conPannello ? (
          /* Sul telefono, la forma del pannello del Rullo: etichetta,
             il campo (che qui apre il pannello a schermo intero, dove si
             scrive e si filtra insieme) e sotto «Filtri (n)». Era un
             bottone a bordo sabbia col testo guida in monospazio e
             «FILTRA» in maiuscoletto. */
          <button
            type="button"
            onClick={() => setPannelloAperto(true)}
            className={`w-full min-w-0 cursor-pointer border border-rule bg-surface p-4 text-left sm:hidden ${FUOCO}`}
          >
            <span className={ETICHETTA_FACCETTA}>{ariaLabel}</span>
            <span className="flex min-h-[52px] items-center border border-sommesso/60 bg-bg px-[15px]">
              <span className="min-w-0 flex-1 truncate font-sans text-titolo text-muted">
                {testoUrl || segnaposto}
              </span>
            </span>
            {menu.length > 0 ? (
              <span className="mt-3 flex min-h-[44px] items-center justify-between border border-rule bg-bg px-4 font-sans text-voce font-medium text-ink">
                <span>
                  Filtri <span className="tabular-nums">({quantiAccesi})</span>
                </span>
                <IconaGiu aperto={false} />
              </span>
            ) : null}
          </button>
        ) : null}


        {pannelloAperto ? (
          <PannelloRicerca
            menu={menu.map((m) => ({ ...m, valore: mostrati[m.param] ?? m.valore }))}
            testo={testoUrl}
            segnaposto={segnaposto}
            onChiudi={() => setPannelloAperto(false)}
            onApplica={(q, valori) => {
              setPannelloAperto(false);
              if (props.modo === "locale") {
                props.onTesto(q);
                for (const [k, v] of Object.entries(valori))
                  props.onMenu?.(k, v);
                return;
              }
              /* Testo e restringimenti arrivano insieme e diventano
                 **una** navigazione. Passando per i singoli `onChange`
                 ne partirebbero quattro di fila, con tre voci di
                 cronologia spurie e tre render buttati. */
              const cambi: Record<string, string> = { q: q.trim() };
              for (const [k, v] of Object.entries(valori))
                cambi[k] = Array.isArray(v) ? v.join(",") : v;
              if (cambi.place) {
                cambi.etype = "";
                cambi.entity = "";
              }
              if (attesa.current) clearTimeout(attesa.current);
              vaiA(componi(cambi));
            }}
          />
        ) : null}

        {laCornice}
      </div>

      {/* Quello che nella cornice non ci sta.

          Il conto sotto `lg`: là la cornice è già stretta e un dato in
          più le toglierebbe il campo, e sotto `sm` la cornice non c'è
          affatto — c'è il bersaglio che apre il pannello — quindi
          questo è l'unico posto in cui il numero si può leggere.
          La nota sempre: è una frase che spiega dove va a finire la
          ricerca, non un dato da incastrare fra i controlli. */}
      {nota || contatore ? (
        /* Senza nota questa riga esiste **solo** sotto `lg`: sopra, il
           conto è già dentro la cornice e qui resterebbe un `mt-2`
           vuoto, cioè otto pixel di niente sotto la barra. */
        <div
          className={`mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 ${
            nota ? "" : "lg:hidden"
          }`}
        >
          {nota ? (
            <p className="m-0 min-w-0 text-nota uppercase tracking-etichetta text-muted">
              {nota}
            </p>
          ) : null}
          {contatore ? (
            <span className="ml-auto shrink-0 text-nota font-semibold uppercase tracking-dato text-muted lg:hidden">
              {isLoading ? (
                <span className="text-accent">CARICAMENTO…</span>
              ) : (
                contatore
              )}
            </span>
          ) : null}
        </div>
      ) : null}
    </>
  );

  /* Senza fascia la barra è solo la riga: la usa chi la mette dentro
     un blocco che il fondo e il rientro ce li ha già. */
  if (!fascia) return <div className={`my-parti ${className}`}>{riga}</div>;

  return (
    /* L'aria (40px) è **dentro** il componente e non lasciata a chi
       rende: «lascia uno spazio fra le parti, e vale per tutti» non è
       una misura per pagina, è una regola della barra. Prima ogni rotta
       passava il suo stacco nel `className` — chi `mt-band`, chi
       `mt-heading`, chi niente — ed è il modo in cui otto barre uguali
       tornano a sembrare otto.

       **Sopra e sotto, anche quando è appesa.** Ci è voluto un giro per
       arrivarci: il margine di un primo figlio esce dal contenitore che
       non ha padding né bordo in cima (margini che collassano), e sul
       rullo la barra è il primo blocco del `<main>`, quindi i suoi
       quaranta pixel spingevano giù la lastra del guscio — i fianchi
       verticali del sito cominciavano a 161 invece che a 121. Il
       rimedio è stato togliere il margine sopra, e il risultato era una
       barra a undici pixel dalla navigazione e cinquanta dalla lista:
       appiccicata di sopra e sbilanciata. Adesso il margine lo trattiene
       il guscio (`flow-root` sulla lastra in `app/layout.tsx`) e qui
       l'aria torna simmetrica.
       Sul comportamento appeso non cambia niente: `top` si misura sul
       border box, il margine non entra nella scatola appiccicata.

       Margine e non padding, per la ragione opposta: il padding **sì**
       che entra nella scatola appiccicata, e su una barra senza fondo
       resterebbe appeso sotto la testata come una banda trasparente in
       cui il contenuto scorre a vista.

       Con `ancorata` la scelta si rovescia, ed è il punto della prop:
       l'aria sopra diventa **rientro dentro la fascia** (`mb-parti` per
       lo stacco di sotto, e il `pt` sul fondo qui sotto), proprio
       perché il padding entra nella scatola appiccicata. Quello che là
       era il difetto qui è quello che serve: lo spazio fra la
       navigazione e il campo viaggia con la barra invece di restare
       indietro, quindi resta identico a pagina ferma e a pagina
       scorsa. Con il margine, che nella scatola non entra, la barra si
       sarebbe fermata a filo del bordo e i 45 pixel sarebbero
       spariti al primo tocco di rotella. */
    <section
      className={`relative z-[15] no-print ${
        ancorata
          ? "[--oi-coda:1.25rem] mb-[calc(var(--spacing-parti)-var(--oi-coda))]"
          : "my-parti"
      } ${appesa ? (ancorata ? "sticky top-0" : "static md:sticky md:top-0") : ""} ${className}`}
    >
      {/* Il fondo, e basta: **niente filetto sotto e niente rientro
          suo**.

          Il filetto correva per tutta la larghezza del sito e non
          divideva niente. Dove la barra sta in mezzo a una pagina — i
          dossier, gli editoriali, l'archivio — cadeva a quaranta pixel
          dal bordo del frontespizio, cioè due righe lunghe e parallele
          con la barra schiacciata in mezzo: la barra si leggeva come
          incollata su una mascherina invece che appoggiata alla pagina.
          Il rientro lo dà il contenitore di rotta, che ogni pagina ha
          già: qui dentro si sommava a sé stesso.

          Resta il fondo, e serve a una cosa sola: la barra appesa deve
          coprire quello che le scorre sotto. Per questo la fascia si
          accende **solo dove la barra è appesa** — altrove è una lastra
          che non copre niente. */}
      {/* Il fondo è **esattamente la riga**: niente padding verticale.
          Quei dieci pixel per parte si sommavano ai sessanta del
          margine e facevano della barra del rullo l'unica con
          settanta di aria invece di sessanta — misurato: 70/70 contro
          i 60/60 di tutte le altre. La fascia la porta il solo rullo,
          quindi il suo respiro interno era una differenza che nessuna
          altra rotta aveva.
          Il fondo resta perché è il suo mestiere: da appesa copre
          quello che le scorre sotto, riga di nota compresa.

          **Con `ancorata` il fondo comincia più in alto.** Lo stacco
          dalla navigazione è un rientro di questa scatola e non un
          margine della sezione, quindi è dipinto: la fascia appesa è
          alta quanto lo stacco più la riga, e sopra il campo non si
          apre nessuna finestra trasparente in cui si vedono passare i
          dossier. Il valore è lo stesso token dello stacco di sotto
          (`--spacing-parti`, qui tre quarti), così i due lati della
          barra restano una misura sola. */}
      {/* `bg-bg` e non `bg-surface`: con la tavolozza di prima le due
          quasi coincidevano (1,07:1) e la fascia non si vedeva; con la
          carta calda di tutto il sito (30/09/2026) la carta è più chiara
          del fondo e sopra la barra si apriva una striscia di 45 pixel
          che non era niente. Coprire quello che scorre sotto lo fa
          uguale anche il fondo della pagina. */}
      <div className={`bg-bg ${ancorata ? "pt-parti" : ""}`}>{riga}</div>

      {/* La coda della fascia: venti pixel del **fondo della pagina**,
          appesi insieme al resto.
          Serve a una cosa che si vede solo scorrendo: le schede dei
          dossier hanno il bordo colorato, e arrivando sotto la barra
          quel bordo finiva a filo del fondo della riga: due linee
          attaccate, una colorata e una no, che si leggevano come un
          errore di stampa. Con la coda la scheda sparisce venti pixel
          prima di toccare la barra, e quello che resta fra le due è
          carta della pagina.
          Non è un filetto e non deve diventarlo: è lo stesso colore di
          quello che ha sotto, quindi a pagina ferma non si vede — si
          vede solo il suo effetto, cioè che sotto la ricerca non passa
          niente.
          L'altezza esce dal margine di sotto (`--oi-coda` nel `calc`
          della sezione) invece di aggiungersi: a pagina ferma lo
          stacco fra la barra e il primo blocco resta quello di prima,
          perché venti pixel di carta dipinta e venti di margine
          trasparente sopra la stessa carta sono la stessa cosa. Così
          questa aggiunta non sposta niente.
          `static` e senza `z-index`: i pannelli dei filtri sono figli
          posizionati della riga qui sopra e si dipingono comunque sopra
          un fondo non posizionato, anche se nel DOM vengono prima. */}
      {ancorata ? (
        <span aria-hidden className="block h-[var(--oi-coda)] bg-bg" />
      ) : null}
    </section>
  );
}
